import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useStorefrontFonts from '../../../../hooks/useStorefrontFonts';
import { collectionRows } from './canvasModel';
import { libraryCSS, materializePackage } from './sectionLibrary';
import { chromeKind, websiteContentTheme } from './websiteChrome';

const NATIVE_ACTIONS = { productConfigurator: 'commerce.configure', cart: 'commerce.cart.open', fulfilmentSelector: 'fulfilment', customerSignIn: 'customer.signIn' };
const LABELS = { productConfigurator: 'Choose options', cart: 'View cart', fulfilmentSelector: 'Delivery and pickup', customerSignIn: 'Sign in' };
const safeImage = (value) => typeof value === 'string' && (/^data:image\/(png|jpeg|webp|gif);base64,/.test(value) || /^https:\/\//.test(value) || /^\/(?!\/)/.test(value));

// Keep React out of the focused text node so live updates preserve the caret.
export function CanvasText({ value, path, onEdit, onSelect }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && ref.current !== ref.current.getRootNode().activeElement) ref.current.textContent = value;
  }, [value]);
  return <span ref={ref} data-canvas-path={path} data-canvas-text="" contentEditable suppressContentEditableWarning
    role="textbox" aria-label="Edit website text" tabIndex={0}
    onFocus={() => onSelect(path)}
    onInput={(e) => onEdit(path, e.currentTarget.textContent)}
    onPaste={(e) => { e.preventDefault(); document.execCommand('insertText', false, e.clipboardData.getData('text/plain')); }}
    onKeyDown={(e) => { if (e.key === 'Escape') e.currentTarget.blur(); }} />;
}

// Parsed into React text/elements only: no HTML injection, even for rich text settings.
function richText(value) {
  const parsed = document.createElement('template');
  parsed.innerHTML = String(value);
  const allowed = new Set(['P','BR','STRONG','EM','UL','OL','LI']);
  const walk = (node, i) => {
    if (node.nodeType === 3) return node.textContent;
    if (['SCRIPT','STYLE','IFRAME','OBJECT'].includes(node.nodeName)) return null;
    const children = Array.from(node.childNodes).map(walk);
    return allowed.has(node.nodeName) ? React.createElement(node.nodeName.toLowerCase(), { key: i }, node.nodeName === 'BR' ? undefined : children) : children;
  };
  return Array.from(parsed.content.childNodes).map(walk);
}

export default function WebsiteRenderer({ website, catalogue = {}, onAction, disabled = false, onNavigate, initialPage, initialSection, frameContent, onContentTheme }) {
  const isFrame = frameContent !== undefined;
  const ownsPage = !onNavigate;
  const RootTag = onNavigate ? 'div' : 'main';
  const host = useRef(null);
  const [root, setRoot] = useState(null);
  const { package: sourcePackage, edits } = website;
  const pkg = useMemo(() => materializePackage(sourcePackage,edits),[sourcePackage,edits.layout]); // eslint-disable-line react-hooks/exhaustive-deps
  const canvas = website.canvas;
  const canvasSelection = useRef(canvas?.selection);
  canvasSelection.current = canvas?.selection;
  const editing = !!canvas?.editMode && !!website.store_id && window.parent !== window;
  const send = (type, detail) => window.parent.postMessage({type, draftId:website.id, storeId:website.store_id, ...detail}, window.location.origin);
  // Fragments come from the URL; inherited keys such as #constructor are not pages.
  const entryPage = Object.prototype.hasOwnProperty.call(pkg.pages, initialPage) ? initialPage : pkg.manifest.entry;
  const [pageKey, setPageKey] = useState(entryPage);
  useEffect(() => { setRoot(host.current.shadowRoot || host.current.attachShadow({ mode: 'open' })); }, []);
  useEffect(() => { setPageKey(entryPage); }, [pkg, entryPage]);
  useEffect(() => { if (ownsPage) window.scrollTo({top:0,behavior:'instant'}); }, [pageKey, ownsPage]);
  useEffect(() => {
    if (root && initialSection && !Object.prototype.hasOwnProperty.call(pkg.pages, initialSection)) root.getElementById(initialSection)?.scrollIntoView({ block: 'start' });
  }, [root, pageKey, initialSection, pkg]);
  useEffect(() => {
    if (!root || !isFrame || !onContentTheme) return;
    const site = root.querySelector('[data-plateos-site]');
    if (site) onContentTheme(websiteContentTheme(getComputedStyle(site)));
  }, [root, isFrame, onContentTheme, pkg, edits.settings]);
  useEffect(() => { if (pkg.pages[canvas?.page]) setPageKey(canvas.page); }, [canvas?.page, pkg]);
  useEffect(() => {
    if (!root || !canvas?.focusToken) return;
    const frame = requestAnimationFrame(() => {
      const selected = Array.from(root.querySelectorAll('[data-canvas-path]')).find((el) => el.dataset.canvasPath === canvasSelection.current?.path);
      const section = root.getElementById(canvasSelection.current?.section);
      (selected || section)?.scrollIntoView({block:'center', behavior:'auto'});
    });
    return () => cancelAnimationFrame(frame);
  }, [root, canvas?.focusToken, pageKey]);
  const settings = Object.fromEntries(Object.entries(pkg.manifest.settings).map(([key, spec]) => [key, edits.settings[key] ?? spec.default]));
  useStorefrontFonts(Object.entries(pkg.manifest.settings).filter(([,spec]) => spec.type === 'font').map(([key]) => settings[key]));
  // Browsers do not consistently activate @font-face from shadow stylesheets.
  // Register only the already-validated bundled font sources, in this storefront
  // document. The admin preview is a separate iframe document.
  useEffect(() => {
    if (!root || typeof FontFace === 'undefined') return undefined;
    let disposed = false;
    const faces = [];
    const visit = (rules) => Array.from(rules || []).forEach((rule) => {
      if (rule.type === 5 && faces.length < 30) {
        const family = rule.style.getPropertyValue('font-family').replace(/^['"]|['"]$/g,'');
        const source = rule.style.getPropertyValue('src');
        if (!source.includes('data:font/')) return;
        try {
          const face = new FontFace(family,source,{style:rule.style.fontStyle || 'normal',weight:rule.style.fontWeight || 'normal',display:'swap'});
          faces.push(face);
          face.load().then(() => {if (!disposed) document.fonts.add(face);}).catch(() => {});
        } catch (_e) { /* Invalid font binaries remain a conversion-review issue. */ }
      } else if (rule.cssRules) visit(rule.cssRules);
    });
    root.querySelectorAll('style').forEach((style) => visit(style.sheet?.cssRules));
    return () => { disposed = true; faces.forEach((face) => document.fonts.delete(face)); };
  }, [root, pkg.css]);
  const byRef = Object.fromEntries(Object.values(catalogue).flat().map((p) => [p.ref, p]));
  const products = Object.fromEntries(Object.entries(pkg.bindings.products).map(([key, spec]) => [key, (edits.products[key] ? (catalogue[spec.kind] || []).find((p) => p.id === edits.products[key]) : (catalogue[spec.kind] || [])[0])]));
  const resolve = (value, context) => {
    if (!value || typeof value !== 'object') return value ?? '';
    if (value.setting) return settings[value.setting] ?? '';
    const path = value.data.split('.');
    const product = path[0] === 'products' ? products[path[1]] : context[path[0]];
    return product?.[path[path.length - 1]] ?? '';
  };
  const act = (action, target) => {
    // A directory stays mounted on its own route, so close a disclosure menu
    // even when its Full menu action navigates back to that same route.
    root?.querySelectorAll('details[open]').forEach(element => element.removeAttribute('open'));
    if (onNavigate && action.startsWith('navigate.') && action!=='navigate.external') {onNavigate(action,target);return;}
    if (action === 'navigate.page') { setPageKey(target); if (canvas) send('PLATEOS_WEBSITE_CANVAS_PAGE', {page:target}); return; }
    if (action === 'navigate.section') { root?.getElementById(target)?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }); return; }
    if (disabled) return;
    if (action === 'navigate.external') { window.open(target, '_blank', 'noopener,noreferrer'); return; }
    onAction(action, byRef[target]);
  };
  const render = (node, context = {}, key = 'root', path = '') => {
    const override = edits.blocks?.[path] || {};
    const attributes = editing ? {'data-canvas-path':path, 'data-canvas-selected':canvas.selection?.path === path ? '' : undefined} : {};
    const children = () => (node.children || []).map((n, i) => render(n, context, `${key}-${i}`, `${path}/children/${i}`));
    if (node.type === 'text') {
      const value = override.text ?? resolve(node.value, context);
      const plainEditable = typeof node.value === 'string' || (node.value?.setting && pkg.manifest.settings[node.value.setting].type === 'text');
      if (editing && plainEditable) return <CanvasText key={key} value={String(value)} path={path} onSelect={(nodePath) => send('PLATEOS_WEBSITE_CANVAS_SELECT', {page:pageKey,section:nodePath.split('/')[1],path:nodePath})} onEdit={(nodePath, text) => send('PLATEOS_WEBSITE_CANVAS_TEXT', {path:nodePath, text})} />;
      return node.value?.setting && pkg.manifest.settings[node.value.setting].type === 'richText' ? <React.Fragment key={key}>{richText(value)}</React.Fragment> : <React.Fragment key={key}>{String(value)}</React.Fragment>;
    }
    if (node.type === 'element') return React.createElement(node.tag, { ...node.attrs, ...attributes, style:override.style, key, className: node.className }, ['br','hr'].includes(node.tag) ? undefined : children());
    if (node.type === 'image') {
      const ref = override.image ?? resolve(node.src, context);
      const src = pkg.assets[ref] || ref;
      return safeImage(src) ? <img {...attributes} style={override.style} key={key} className={node.className} src={src} alt={String(resolve(node.alt, context))} loading="lazy" /> : editing ? <div {...attributes} key={key} style={{padding:24, background:'#f1f5f9', color:'#334155'}}>{node.src?.data ? 'Product has no image' : 'Choose an image'}</div> : null;
    }
    if (node.type === 'repeat') {
      const definition = pkg.bindings.collections[node.collection];
      const rows = collectionRows(node, override, definition, edits, catalogue);
      const content = rows.length ? rows.map((p) => render(node.template, { ...context, [node.as]: p }, `${key}-${p.ref}`, `${path}/template`)) : render(node.empty, context, `${key}-empty`, `${path}/empty`);
      return editing ? <div {...attributes} key={key} style={{display:'contents'}}>{content}</div> : <React.Fragment key={key}>{content}</React.Fragment>;
    }
    const action = node.type === 'native' ? NATIVE_ACTIONS[node.component] : (override.action || node.action);
    const target = resolve(override.target ?? node.target, context);
    const unavailable = action === 'commerce.configure' && !byRef[target]?.purchasable;
    return <button {...attributes} style={override.style} key={key} type="button" className={node.className} disabled={!editing && (unavailable || (disabled && !action.startsWith('navigate.')))} onClick={() => { if (!editing) act(action, target); }}>
      {node.type === 'native' ? (resolve(node.label, context) || LABELS[node.component]) : children()}
    </button>;
  };
  const page = pkg.pages[pageKey] || pkg.pages[pkg.manifest.entry];
  const sectionMap = Object.fromEntries(page.sections.map((s) => [s.id, s]));
  const sectionOrder = edits.sections[pageKey] || page.sections;
  const tokens = Object.fromEntries(Object.entries(pkg.manifest.tokens).map(([name, value]) => [name, settings[value.setting]]));
  const selectElement = (event) => {
    if (!editing) return;
    // Shadow DOM retargets event.target to its host at the React boundary.
    // The composed path retains the actual clicked element inside the design.
    const hit = event.nativeEvent.composedPath()[0];
    const target = hit?.nodeType === 3 ? hit.parentElement : hit;
    const element = target?.closest?.('[data-canvas-path]');
    const section = target?.closest?.('[data-canvas-section]');
    if (!section) return;
    // Let FAQ disclosure and contenteditable focus work; never activate commerce.
    if (target.closest?.('button,a') && !target.closest?.('[contenteditable]')) event.preventDefault();
    event.stopPropagation();
    send('PLATEOS_WEBSITE_CANVAS_SELECT', {page:pageKey, section:section.id, path:element?.dataset.canvasPath || `${pageKey}/${section.id}/root`});
  };
  const renderSection = s => <section id={s.id} key={s.id} aria-label={sectionMap[s.id].label} data-canvas-section={editing ? '' : undefined} data-selected-section={editing && canvas.selection?.section === s.id ? '' : undefined}>{render(sectionMap[s.id].root, {}, s.id, `${pageKey}/${s.id}/root`)}</section>;
  const enabledSections = sectionOrder.filter(s => s.enabled && sectionMap[s.id]);
  return <div ref={host} data-testid="website-package-renderer" style={{ isolation: 'isolate', contain: 'layout paint', display: 'block', minHeight: 100 }}>
    {isFrame && <div slot="native-content" data-testid="website-native-content">{frameContent}</div>}
    {root && createPortal(<>
      <style>{`:host{all:initial;display:block;color:${onNavigate ? 'inherit' : '#171717'};font-family:${onNavigate ? 'inherit' : 'Arial,sans-serif'}}*{box-sizing:border-box}img{max-width:100%;height:auto}button{cursor:pointer;font:inherit}button:disabled{cursor:not-allowed;opacity:.55}[data-plateos-site]{overflow-wrap:anywhere} @media(prefers-reduced-motion:reduce){*,*::before,*::after{animation:none!important;transition:none!important;scroll-behavior:auto!important}}`}</style>
      <style>{pkg.css}</style>
      <style>{libraryCSS}</style>
      {editing && <style>{`[data-canvas-path]:hover{outline:1px dashed #0d9488;outline-offset:-2px}[data-canvas-selected]{outline:2px solid #0d9488!important;outline-offset:-2px}[data-canvas-text]{cursor:text;white-space:pre-wrap}[data-canvas-text]:empty{display:inline-block;min-width:12px}[data-canvas-text]:focus{outline:2px solid #0d9488;background:#0d948810}[data-canvas-section]{position:relative}[data-canvas-section][data-selected-section]{box-shadow:inset 0 0 0 2px #0d9488}`}</style>}
      <RootTag data-plateos-site="" style={tokens} onClickCapture={selectElement}>{isFrame ? <>
        {enabledSections.filter(s => chromeKind(sectionMap[s.id]) === 'header').map(renderSection)}
        <slot name="native-content" />
        {enabledSections.filter(s => chromeKind(sectionMap[s.id]) === 'footer').map(renderSection)}
      </> : enabledSections.map(renderSection)}</RootTag>
    </>, root)}
  </div>;
}
